import React from 'react';
import { useDispatch } from 'react-redux';
import { ArcSocketContext } from '@src/core/socket';
import { EMPTY_VALUE } from '@src/core/config';
import { EnRouteCommandEnum, EnRouteEventTypeEnum } from '@src/features/order/pages/take-off-to-en-route-page/entities';
import { setJoystickStatus } from '@src/features/order/pages/checklist-page/redux';
import { useGetUserSettingQuery } from '@src/features/settings/redux/api';
import { trackError, toTrackedError } from '@src/core/sentry';
import {
  CameraActionEnum,
  CameraControlKey,
  ControllerModeEnum,
  DEFAULT_CAMERA_CONTROLS,
  CAMERA_CONTROL_BUTTON_INDEX,
  MODE_AXES_MAPPING,
  USER_SETTING_KEYS,
  applyDeadZone,
} from '@src/features/settings/pages/controller-page/config/controller-constants';

export const useJoystick = (serialNumber?: string, isManualControl?: boolean) => {
  const dispatch = useDispatch();
  const { arcSocket } = React.useContext(ArcSocketContext);

  const [connected, setConnected] = React.useState(false);
  const [joystickId, setJoystickId] = React.useState<string>(EMPTY_VALUE);

  const [axes, setAxes] = React.useState<number[]>([]);
  const [buttons, setButtons] = React.useState<GamepadButton[]>([]);

  const { data: modeData } = useGetUserSettingQuery(USER_SETTING_KEYS.CONTROLLER_MODE);
  const { data: cameraData } = useGetUserSettingQuery(USER_SETTING_KEYS.CAMERA_CONTROLS);

  const controllerMode = React.useMemo<ControllerModeEnum>(() => {
    const value = modeData?.value as ControllerModeEnum;
    if (value && Object.values(ControllerModeEnum).includes(value)) {
      return value;
    }
    return ControllerModeEnum.MODE_2;
  }, [modeData]);

  const cameraControls = React.useMemo<Record<CameraControlKey, CameraActionEnum>>(() => {
    if (cameraData?.value) {
      try {
        return JSON.parse(cameraData.value);
      } catch (error: unknown) {
        trackError(toTrackedError(error), { code: 'FE_0024', extra: { source: 'useJoystick' } });
        return DEFAULT_CAMERA_CONTROLS;
      }
    }
    return DEFAULT_CAMERA_CONTROLS;
  }, [cameraData]);

  const axesMapping = React.useMemo(() => MODE_AXES_MAPPING[controllerMode], [controllerMode]);

  const buttonActionMap = React.useMemo(() => {
    const map: Record<number, CameraActionEnum> = {};
    (Object.keys(cameraControls) as CameraControlKey[]).forEach((key) => {
      const buttonIndex = CAMERA_CONTROL_BUTTON_INDEX[key];
      if (buttonIndex !== undefined) {
        map[buttonIndex] = cameraControls[key];
      }
    });
    return map;
  }, [cameraControls]);

  const prevButtonState = React.useRef<Record<number, boolean>>({});
  const prevConnectedRef = React.useRef<boolean>(false);

  React.useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const pollGamepad = () => {
      const socket = arcSocket();
      const gamepads = navigator.getGamepads();
      const currentGamepad = gamepads[0];

      if (currentGamepad && socket && socket.readyState === WebSocket.OPEN) {
        const { id, buttons: gpButtons, axes: gpAxes } = currentGamepad;

        const nextAxes = Array.from(gpAxes, a => applyDeadZone(Number(a.toFixed(2))));
        setAxes(prev => {
          if (prev.length === nextAxes.length) {
            let same = true;
            for (let i = 0; i < prev.length; i++) {
              if (prev[i] !== nextAxes[i]) { same = false; break; }
            }
            if (same) return prev;
          }
          return nextAxes;
        });

        setButtons(prev => {
          if (prev.length === gpButtons.length) {
            let same = true;
            for (let i = 0; i < prev.length; i++) {
              const a = prev[i];
              const b = gpButtons[i];
              if (!a || a.pressed !== b.pressed || a.value !== b.value || a.touched !== b.touched) {
                same = false;
                break;
              }
            }
            if (same) return prev;
          }
          return Array.from(gpButtons);
        });

        // Camera button actions (based on saved settings)
        Object.entries(buttonActionMap).forEach(([indexStr, action]) => {
          const buttonIndex = Number(indexStr);
          const button = gpButtons[buttonIndex];
          if (!button || action === CameraActionEnum.NONE) return;

          const isPressed = button.pressed || button.value >= 1;
          const wasPressed = prevButtonState.current[buttonIndex] ?? false;

          if (isPressed && !wasPressed && isManualControl) {
            switch (action) {
            case CameraActionEnum.ZOOM_IN:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.ZOOM_CONTROL,
                serialNumber,
                zoom: 'in',
              }));
              break;
            case CameraActionEnum.ZOOM_OUT:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.ZOOM_CONTROL,
                serialNumber,
                zoom: 'out',
              }));
              break;
            case CameraActionEnum.TILT_UP:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.GIMBAL_CONTROL,
                serialNumber,
                pitch: 5,
              }));
              break;
            case CameraActionEnum.TILT_DOWN:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.GIMBAL_CONTROL,
                serialNumber,
                pitch: -5,
              }));
              break;
            case CameraActionEnum.PAN_LEFT:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.GIMBAL_CONTROL,
                serialNumber,
                yaw: -5,
              }));
              break;
            case CameraActionEnum.PAN_RIGHT:
              socket.send(JSON.stringify({
                type: EnRouteEventTypeEnum.CAMERA_COMMAND,
                command: EnRouteCommandEnum.GIMBAL_CONTROL,
                serialNumber,
                yaw: 5,
              }));
              break;
            case CameraActionEnum.START_STOP_VIDEO_RECORDING:
              break;
            }
          }

          prevButtonState.current[buttonIndex] = isPressed;
        });

        // Manual control with dynamic axes mapping
        const rawAxes = gpAxes.map((axis) => applyDeadZone(Number(axis.toFixed(2))));
        const yaw = rawAxes[axesMapping.yaw] ?? 0;
        const throttle = rawAxes[axesMapping.throttle] ?? 0;
        const roll = rawAxes[axesMapping.roll] ?? 0;
        const pitch = rawAxes[axesMapping.pitch] ?? 0;

        if (isManualControl) {
          socket.send(
            JSON.stringify({
              type: EnRouteEventTypeEnum.MANUAL_CONTROL_COMMAND,
              keys: [pitch, roll, yaw, throttle],
              serialNumber,
            })
          );
        }
        setConnected(true);
        setJoystickId(id);

        if (serialNumber && !prevConnectedRef.current) {
          dispatch(setJoystickStatus({
            serialNumber,
            connected: true,
            joystickId: id,
          }));
          prevConnectedRef.current = true;
        }
      } else {
        setConnected(false);
        setJoystickId(EMPTY_VALUE);

        if (serialNumber && prevConnectedRef.current) {
          dispatch(setJoystickStatus({
            serialNumber,
            connected: false,
            joystickId: null,
          }));
          prevConnectedRef.current = false;
        }
      }
    };

    intervalId = setInterval(pollGamepad, 150);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
        setConnected(false);
        setJoystickId(EMPTY_VALUE);
        prevConnectedRef.current = false;
      }
    };
  }, [isManualControl, serialNumber, dispatch, axesMapping, buttonActionMap]);

  return { axes, buttons, connected, joystickId };
};

export default useJoystick;